import { Suspense, lazy, useEffect } from 'react';
import './App.scss';
import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import Sidebar from './components/Common/Sidebar';
import DummyButton from './pages/DummyButton';
import PageLoading from './components/Common/PageLoading';
import Payment from './components/AdSetup/PaymentGateway';
import SuccessPayment from './components/AdSetup/SuccessPayment';
import { useDispatch } from 'react-redux';
import { setColorChange } from './components/features/AddCampaignSlice';
import { DummyAuthProvider } from './components/Auth/AuthContext';
import { RoleGuard } from './components/Auth/RoleGuard';

const AdvertiserDashboard = lazy(() => import('./pages/AdvertiserDashboard'));
const AdvertiserCampaignAnalytics = lazy(() => import('./pages/AdvertiserCampaignAnalytics').then(m => ({ default: m.AdvertiserCampaignAnalytics })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const SupervisorDashboard = lazy(() => import('./pages/SupervisorDashboard'));
const SupervisorCampaignWorkspace = lazy(() => import('./pages/SupervisorCampaignWorkspace'));
const DeveloperLogin = lazy(() => import('./pages/DeveloperLogin'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AddCampaigns = lazy(() => import('./pages/AddCampaigns'));
const AdSetup = lazy(() => import("./pages/AdSetup"));
const CampaignAnalytics = lazy(() => import('./pages/CampaignAnalytics'));
const NewGraphQLPage = lazy(() => import('./pages/NewGraphQLPage'));

function App() {
  const dispatch = useDispatch();
  const take = useLocation();

  useEffect(() => {
    dispatch(setColorChange(false));
  }, [dispatch]);

  return (
    <DummyAuthProvider>
      <div style={{ background: take.pathname === '/payment' ? '#fff' : undefined, minHeight: take.pathname === '/payment' ? "100vh" : undefined }}>
        <Routes>
          <Route path='/' element={take.pathname !== "/" && take.pathname !== "/new" && take.pathname !== "/login" && take.pathname !== "/demo-login" ? <Sidebar /> : null}>
            <Route path='/' element={<DummyButton />} />
            
            {/* Dedicated Role Login Routes */}
            <Route path='/login' element={<Suspense fallback={<PageLoading />}><LoginPage /></Suspense>} />
            <Route path='/admin/login' element={<Suspense fallback={<PageLoading />}><LoginPage /></Suspense>} />
            <Route path='/supervisor/login' element={<Suspense fallback={<PageLoading />}><LoginPage /></Suspense>} />
            <Route path='/demo-login' element={<Suspense fallback={<PageLoading />}><LoginPage /></Suspense>} />

            {/* Distinct Dedicated Role Page 1: Advertiser Dashboard */}
            <Route path='/manage-campaigns' element={<Suspense fallback={<PageLoading />}><AdvertiserDashboard /></Suspense>} />
            <Route path='/advertiser/campaign/:campaignId' element={<Suspense fallback={<PageLoading />}><AdvertiserCampaignAnalytics /></Suspense>} />

            {/* Distinct Dedicated Role Page 2: Admin Global Dashboard */}
            <Route
              path='/admin-dashboard'
              element={
                <RoleGuard allowedRoles={['Admin']}>
                  <Suspense fallback={<PageLoading />}><AdminDashboard /></Suspense>
                </RoleGuard>
              }
            />

            {/* Distinct Dedicated Role Page 3: Supervisor Dashboard */}
            <Route
              path='/supervisor-dashboard'
              element={
                <RoleGuard allowedRoles={['Supervisor']}>
                  <Suspense fallback={<PageLoading />}><SupervisorDashboard /></Suspense>
                </RoleGuard>
              }
            />

            {/* Dedicated Role Page 4: Supervisor & Admin Campaign Analytics Workspace */}
            <Route
              path='/supervisor/campaign/:campaignId'
              element={
                <RoleGuard allowedRoles={['Supervisor', 'Admin']}>
                  <Suspense fallback={<PageLoading />}><SupervisorCampaignWorkspace /></Suspense>
                </RoleGuard>
              }
            />

            {/* Creation Wizard Routes */}
            <Route path='/addcampaigns' element={<Suspense fallback={<PageLoading />}><AddCampaigns /></Suspense>} />
            <Route path='/addcampaigns/adsetup' element={<Suspense fallback={<PageLoading />}><AdSetup /></Suspense>} />

            {/* Universal Campaign Edit Routes (Reusing Creation Wizard for all Roles) */}
            <Route
              path='/edit-campaign/:id'
              element={
                <RoleGuard allowedRoles={['Advertiser', 'Admin', 'Supervisor']}>
                  <Suspense fallback={<PageLoading />}><AddCampaigns isEditMode={true} /></Suspense>
                </RoleGuard>
              }
            />
            <Route
              path='/edit-campaign/:id/adsetup'
              element={
                <RoleGuard allowedRoles={['Advertiser', 'Admin', 'Supervisor']}>
                  <Suspense fallback={<PageLoading />}><AdSetup isEditMode={true} /></Suspense>
                </RoleGuard>
              }
            />


            <Route path='/new' element={<Suspense fallback={<PageLoading />}><NewGraphQLPage /></Suspense>} />
            <Route path='/payment' element={<Payment />} />
            <Route path='/success' element={<SuccessPayment />} />
            <Route path='/campaign-analytics' element={<Suspense fallback={<PageLoading />}><CampaignAnalytics /></Suspense>} />
            
            <Route path='/*' element={<Navigate to="/manage-campaigns" />} />
          </Route>
        </Routes>
      </div>
    </DummyAuthProvider>
  );
}

export default App;
